import {
  add,
  compose,
  curry,
  head,
  keys,
  last,
  length,
  map,
  pathOr,
  prop,
  propOr,
  reduce,
  subtract,
  values,
} from 'ramda';

import { path, splash } from '../helpers/compact';
import { getDocById, getDocsByIds, getDocsByUids } from './entities';
import { getLocale } from './i18n';

const getPagers = prop('search');

const getLocalizedPagers = (state) =>
  compose(prop(getLocale(state)), getPagers)(state);

export const getSearchPager = curry((key, state) =>
  prop(key, getLocalizedPagers(state))
);

export const getLastQueryString = (key, state) =>
  compose(
    prop('query'),
    prop('query'),
    prop('options'),
    getSearchPager(key)
  )(state);

export const getOptionsFilters = (key, state) =>
  compose(prop('filters'), prop('options'), getSearchPager(key))(state);

export const isLoading = (key, page, state) =>
  compose(prop(page), prop('loading'), getSearchPager(key))(state);

export const isAnyPageLoading = (key, state) =>
  reduce(
    (a, b) => a || b,
    false,
    values(propOr({}, 'loading', getSearchPager(key, state)))
  );

export const isAnyPageError = (key, state) =>
  reduce(
    (a, b) => a || b,
    null,
    values(propOr({}, 'error', getSearchPager(key, state)))
  );

export const getError = (key, page, state) =>
  compose(prop(page), prop('error'), getSearchPager(key))(state);

const getAvailablePages = compose(keys, propOr([], 'items'), getSearchPager);

export const getNextPage = (key, state) => {
  const items = getAvailablePages(key, state);
  if (!length(items)) {
    return 0;
  }
  return add(last(items), 1);
};

export const getPreviousPage = (key, state) => {
  const items = getAvailablePages(key, state);
  if (!length(items)) {
    return 0;
  }
  return subtract(head(items), 1);
};

export const getTotalPage = (key, state) =>
  compose(path('last.total_page'), getSearchPager(key))(state);

export const getCurrentPage = curry((key, state) =>
  compose(path('last.page'), getSearchPager(key))(state)
);

export const getNextStatus = (key, state) => {
  const nextPage = getNextPage(key, state);
  const totalPage = path('last.total_page', getSearchPager(key, state));

  return {
    page: nextPage,
    isLoading: isLoading(key, nextPage, state),
    error: getError(key, nextPage, state),
    hasReachedEnd: nextPage > totalPage,
  };
};

export const getPreviousStatus = (key, state) => {
  const previousPage = getPreviousPage(key, state);

  return {
    page: previousPage,
    isLoading: isLoading(key, previousPage, state),
    error: getError(key, previousPage, state),
    hasReachedEnd: previousPage <= 0,
  };
};

export const getResults = (key, state) =>
  compose(splash, prop('items'), getSearchPager(key))(state);

export const getResultsCount = (key, state) =>
  compose(pathOr(0, ['last', 'count']), getSearchPager(key))(state);

export const getFacets = (key, state) =>
  compose(path('last.facets'), getSearchPager(key))(state);

export const mapResults = curry((f, key, state) =>
  map(f, getResults(key, state))
);

const getFullResultsWith = (fn) =>
  curry((key, state) => {
    const res = getResults(key, state);
    return fn(state, res).filter(Boolean);
  });

export const getFullResultsByUids = getFullResultsWith(getDocsByUids);

export const getFullResultsByIds = getFullResultsWith(getDocsByIds);

const getDocsByTheirOwnIdentifier = (state, items) =>
  map((item) => getDocById(prop('type', item), prop('id', item), state), items);

export const getFullResultsByTheirOwnIdentifier = getFullResultsWith(
  getDocsByTheirOwnIdentifier
);

export const hasReachedEnd = (key, state) => {
  const request = prop('last', getSearchPager(key, state));
  const { page, total_page: total } = request || {};

  return request && page >= total;
};

export const hasSearchBegan = (key, state) => !!getSearchPager(key, state);
